import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, Calendar, MapPin } from "@phosphor-icons/react";

/**
 * Admin-only manager for BSVAC Triage Events. Renders as a full-screen
 * modal so admins can create, rename, or archive events + edit their
 * treatment centres list without leaving the triage dashboard. Called
 * from TriageDashboard's "Manage Events" button.
 */
export default function TriageEventsAdmin({ open, onOpenChange }) {
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | event object
  const [saving, setSaving] = useState(false);
  const [showPast, setShowPast] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      // Two calls: one filtered (active + upcoming), one full (for the
      // Past Events reveal). Cheap enough given the tiny row counts.
      const [active, all] = await Promise.all([
        api.get("/triage/events", { params: { include_archived: false } }),
        api.get("/triage/events", { params: { include_archived: false, include_past: true } }),
      ]);
      const activeRows = active.data?.events || [];
      const allRows = all.data?.events || [];
      const activeIds = new Set(activeRows.map((e) => e.id));
      setEvents(activeRows);
      setPastEvents(allRows.filter((e) => !activeIds.has(e.id)));
    } catch {
      setEvents([]);
      setPastEvents([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (open) refresh(); }, [open]);

  async function archive(ev) {
    if (!window.confirm(`Archive event "${ev.name}"? It will disappear from the pickers.`)) return;
    try {
      await api.delete(`/triage/events/${ev.id}`);
      toast.success("Event archived");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archive failed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="events-admin-dialog">
        <DialogHeader>
          <DialogTitle>Triage Events</DialogTitle>
          <DialogDescription>Create the events providers can pick from on the ACR form and Command Post.</DialogDescription>
        </DialogHeader>

        {editing ? (
          <EventForm
            event={editing === "new" ? null : editing}
            saving={saving}
            setSaving={setSaving}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
          />
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-slate-500">{loading ? "Loading…" : `${events.length} active event${events.length === 1 ? "" : "s"}`}</div>
              <Button size="sm" className="rounded-sm" onClick={() => setEditing("new")} data-testid="events-new">
                <Plus size={12} weight="bold" className="mr-1" /> New Event
              </Button>
            </div>

            {events.length === 0 && !loading ? (
              <div className="text-center text-slate-500 text-sm py-10 border border-dashed border-slate-200 rounded-sm">
                No events yet. Create one so providers can tag patients with it.
              </div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {events.map((ev) => (
                  <EventRow key={ev.id} ev={ev} onEdit={() => setEditing(ev)} onArchive={() => archive(ev)} />
                ))}
              </div>
            )}

            {/* Past events — collapsed by default; admins can still edit
                or re-open (bump the date) to bring one back to active. */}
            {pastEvents.length > 0 && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowPast((v) => !v)}
                  className="text-[11px] font-bold tracking-widest uppercase text-slate-500 hover:text-slate-700 flex items-center gap-1"
                  data-testid="events-toggle-past"
                >
                  {showPast ? "▼" : "▶"} Past Events ({pastEvents.length})
                </button>
                {showPast && (
                  <div className="space-y-2 mt-2 max-h-[220px] overflow-y-auto opacity-80">
                    {pastEvents.map((ev) => (
                      <EventRow key={ev.id} ev={ev} past onEdit={() => setEditing(ev)} onArchive={() => archive(ev)} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EventRow({ ev, past, onEdit, onArchive }) {
  return (
    <div className={`border rounded-sm px-3 py-2 flex items-start justify-between gap-3 hover:border-slate-400 ${past ? "border-slate-200 bg-slate-50" : "border-slate-200"}`} data-testid={`events-row-${ev.id}`}>
      <div className="min-w-0">
        <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
          {ev.name}
          {past && <span className="text-[9px] font-bold tracking-widest text-slate-400 uppercase">Past</span>}
        </div>
        <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
          {ev.event_date && (
            <span className="flex items-center gap-1">
              <Calendar size={10} weight="bold" />
              {ev.event_date}
              {(ev.event_start_time || ev.event_end_time) && (
                <span className="mono text-slate-400">
                  {" "}
                  · {ev.event_start_time || "—"}–{ev.event_end_time || "—"}
                </span>
              )}
            </span>
          )}
          {ev.location && (
            <span className="flex items-center gap-1"><MapPin size={10} weight="bold" /> {ev.location}</span>
          )}
          <span className="tab-tag text-slate-400">
            Triage Centers ({(ev.triage_centers || []).length}): {(ev.triage_centers || []).join(" · ") || "—"}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="outline" className="rounded-sm h-7 px-2" onClick={onEdit} data-testid={`events-edit-${ev.id}`} title={past ? "Re-open (edit dates)" : "Edit"}>
          <PencilSimple size={12} weight="bold" />
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm h-7 px-2 text-red-600 hover:bg-red-50" onClick={onArchive} data-testid={`events-archive-${ev.id}`} title="Archive">
          <Trash size={12} weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function EventForm({ event, saving, setSaving, onCancel, onSaved }) {
  const [name, setName] = useState(event?.name || "");
  const [eventDate, setEventDate] = useState(event?.event_date || "");
  const [startTime, setStartTime] = useState(event?.event_start_time || "");
  const [endTime, setEndTime] = useState(event?.event_end_time || "");
  const [location, setLocation] = useState(event?.location || "");
  const [notes, setNotes] = useState(event?.notes || "");
  const [centers, setCenters] = useState(event?.triage_centers || []);
  const [newCenter, setNewCenter] = useState("");

  function addCenter() {
    const val = newCenter.trim();
    if (!val || centers.includes(val)) return;
    setCenters([...centers, val]);
    setNewCenter("");
  }

  async function save() {
    if (!name.trim()) { toast.error("Event name is required"); return; }
    const body = {
      name: name.trim(),
      event_date: eventDate || null,
      event_start_time: startTime || null,
      event_end_time: endTime || null,
      location: location || null,
      notes: notes || null,
      triage_centers: centers,
    };
    setSaving(true);
    try {
      if (event?.id) await api.patch(`/triage/events/${event.id}`, body);
      else await api.post("/triage/events", body);
      toast.success(event?.id ? "Event updated" : "Event created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label className="tab-tag">Event Name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" placeholder="e.g. Zero Presents Music Festival" data-testid="events-form-name" />
        </div>
        <div>
          <Label className="tab-tag">Event Date</Label>
          <Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="rounded-sm" data-testid="events-form-date" />
        </div>
        <div>
          <Label className="tab-tag">Location</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} className="rounded-sm" placeholder="e.g. Prospect Park Bandshell" data-testid="events-form-location" />
        </div>
        <div>
          <Label className="tab-tag">Start Time</Label>
          <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="rounded-sm" data-testid="events-form-start-time" />
        </div>
        <div>
          <Label className="tab-tag">End Time <span className="text-slate-400 normal-case font-normal">(event auto-hides after this)</span></Label>
          <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="rounded-sm" data-testid="events-form-end-time" />
        </div>
      </div>

      <div className="mt-3">
        <Label className="tab-tag">Triage Centers</Label>
        <div className="flex gap-2 mt-1">
          <Input value={newCenter} onChange={(e) => setNewCenter(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCenter(); }}}
                 className="rounded-sm" placeholder="Add a center and press Enter" data-testid="events-form-center-input" />
          <Button variant="outline" className="rounded-sm" onClick={addCenter} data-testid="events-form-center-add">
            <Plus size={12} weight="bold" className="mr-1" /> Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {centers.length === 0 ? (
            <span className="text-[11px] text-slate-400">No triage centers yet. Add at least one so providers know where to route patients.</span>
          ) : centers.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 bg-sky-50 border border-sky-200 text-sky-800 text-xs rounded-sm px-2 py-1">
              {c}
              <button type="button" onClick={() => setCenters(centers.filter((x) => x !== c))} className="hover:text-red-600" data-testid={`events-form-center-remove-${c}`}>
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <Label className="tab-tag">Notes</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm" placeholder="Optional — internal notes for command staff." />
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" className="rounded-sm" onClick={onCancel} disabled={saving} data-testid="events-form-cancel">Cancel</Button>
        <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={save} disabled={saving} data-testid="events-form-save">
          {saving ? "Saving…" : (event?.id ? "Update Event" : "Create Event")}
        </Button>
      </DialogFooter>
    </div>
  );
}
